import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

export type RowVersions = Record<string, string>;

export function collectVersions(
  rows: Iterable<{ versions?: RowVersions } | null | undefined>,
): RowVersions | undefined {
  const versions: RowVersions = {};
  for (const row of rows) {
    Object.assign(versions, row?.versions);
  }
  return Object.keys(versions).length ? versions : undefined;
}

export const ON_FARM_IMPLANT_BACKGROUND_REFRESH_MS = 5 * 60 * 1000;

export type OnFarmImplantMatingAllocation = {
  plate: number | null;
  priority: number | null;
  ivcNumber: number | null;
  cleaved: number | null;
  labShipped: number | null;
  heldTransfer: number | null;
  shippedClient: number | null;
  togFrozen: number | null;
  shippedTog: number | null;
  heldFrozen: number | null;
  clientTransfer: number | null;
  clientFrozen: number | null;
  clientDiscarded: number | null;
  discardedGrade1: number | null;
  discardedGrade2: number | null;
  discardedGrade3: number | null;
  degenerates: number | null;
  loadedInStraws: number | null;
  billableShipped: number | null;
  comments: string | null;
  onFarmDiscardedGrade1?: number | null;
  onFarmDiscardedGrade2?: number | null;
  onFarmDiscardedGrade3?: number | null;
  onFarmDegenerates?: number | null;
  onFarmKept?: number;
  version?: string | null;
};

export type OnFarmMatingRecord = {
  id: string;
  recordName: string | null;
  donorName?: string | null;
  sireName?: string | null;
  accountId: string | null;
  accountName: string | null;
  ranchId?: string | null;
  status: string | null;
  date: string | null;
  embryoCount: number;
  allocation?: OnFarmImplantMatingAllocation | null;
  shipmentNumbers?: string | null;
};

export const ON_FARM_UNKNOWN_STAGE = 0;

export type OnFarmImplantStageGradeCount = {
  stage: number;
  grade: number;
  count: number;
};

export type CreateOnFarmImplantFreshEmbryosRequest = {
  matingId: string;
  accountId?: string | null;
  startingStrawNumber: number;
  entries: OnFarmImplantStageGradeCount[];
  autoDiscardGrade2?: boolean;
  autoDiscardGrade3?: boolean;
  versions?: RowVersions;
};

export type OnFarmImplantFreshEmbryo = {
  sessionEmbryoId: string;
  embryoId: string;
  matingId: string;
  matingName: string | null;
  donorName: string | null;
  sireName: string | null;
  accountId: string | null;
  accountName: string | null;
  /** Null for an automatically discarded embryo: discards stay out of the straw sequence. */
  strawNumber: number | null;
  stage: number | null;
  grade: number;
  queueState: OnFarmImplantFreshQueueState;
  canSendToOnFarmFreezing?: boolean;
  versions?: RowVersions;
  editingLockId?: string | null;
  editingBy?: string | null;
};

export type OnFarmImplantFreshQueueState =
  | 'Loaded'
  | 'Implanted'
  | 'Discarded'
  | 'OnFarmFreezing';

export type EditOnFarmImplantFreshEmbryosRequest = {
  sessionEmbryoIds: string[];
  startingStrawNumber?: number | null;
  stage?: number | null;
  grade?: number | null;
  preview?: boolean;
  lockId?: string | null;
  versions?: RowVersions;
};

export type OnFarmImplantFreshEmbryoEdit = {
  sessionEmbryoId: string;
  embryoId: string;
  queueState: OnFarmImplantFreshQueueState;
  oldStrawNumber: number | null;
  newStrawNumber: number | null;
  oldStage: number | null;
  newStage: number | null;
  oldGrade: number | null;
  newGrade: number | null;
};

export type OnFarmImplantFreshEmbryoEditResponse = {
  applied: boolean;
  changes: OnFarmImplantFreshEmbryoEdit[];
  conflictingStrawNumbers: number[];
};

export type OnFarmSourceSummary = {
  sourceType: string;
  items: OnFarmMatingRecord[];
  hasMore: boolean;
};

export type OnFarmSourceTotals = {
  totalRecords: number;
  totalEmbryos: number;
};

export type OnFarmTotalsResponse = {
  fresh: OnFarmSourceTotals;
  frozen: OnFarmSourceTotals;
  flush: OnFarmSourceTotals;
  clientOnFarm: OnFarmSourceTotals;
};

export type MatingsSourceType = 'fresh' | 'frozen' | 'flush' | 'client-on-farm';

export type MatingsQueryParams = {
  search?: string;
  skip?: number;
  take?: number;
  order?: string;
};

export type OnFarmImplantSessionStatus = 'Draft' | 'Active' | 'Completed';

export type OnFarmImplantSession = {
  sessionId: string;
  sessionDate: string;
  siteId: string;
  siteName: string | null;
  accountId: string | null;
  accountName: string | null;
  tripId: string | null;
  tripNumber: string | null;
  status: OnFarmImplantSessionStatus;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  sessionNotes: string | null;
  versions?: RowVersions;
};

export type OnFarmImplantSessionsResponse = {
  items: OnFarmImplantSession[];
  hasMore: boolean;
};

export type OnFarmImplantSessionFilters = {
  siteId?: string;
  accountId?: string;
  tripId?: string;
  createdById?: string;
  sessionDate?: string;
  status?: OnFarmImplantSessionStatus;
  search?: string;
  skip?: number;
  take?: number;
};

export type CreateOnFarmImplantSessionInput = {
  siteId: string;
  sessionDate: string;
  accountId?: string | null;
  tripId?: string | null;
  sessionNotes?: string | null;
};

export type UpdateOnFarmImplantSessionInput = {
  siteId?: string;
  sessionDate?: string;
  accountId?: string;
  clearAccount?: boolean;
  tripId?: string;
  clearTrip?: boolean;
  status?: OnFarmImplantSessionStatus;
  sessionNotes?: string;
  versions?: RowVersions;
};

export type OnFarmImplantTrip = {
  tripId: string;
  tripNumber: string;
  startUtc: string | null;
};

export type OnFarmImplantLookupOption = {
  id: string;
  name: string;
};

export type OnFarmImplantSessionFilterOptions = {
  trips: OnFarmImplantLookupOption[];
  accounts: OnFarmImplantLookupOption[];
  createdBy: OnFarmImplantLookupOption[];
};

function getApiBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getAuthHeaders(token: AccessToken): Record<string, string> {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export class OnFarmImplantRequestError extends Error {
  readonly status: number;
  readonly problems: string[];

  constructor(status: number, problems: string[]) {
    super(`HTTP error! status: ${status}`);
    this.name = 'OnFarmImplantRequestError';
    this.status = status;
    this.problems = problems;
  }
}

export function isStaleVersionError(error: unknown): boolean {
  return error instanceof OnFarmImplantRequestError && error.status === 412;
}

export function invalidateOnFarmImplantSession(
  queryClient: QueryClient,
  sessionId: string,
) {
  return Promise.all(
    [
      'session',
      'selection',
      'session-embryos',
      'queue',
      'fresh-matings',
      'fresh-embryos',
      'on-farm-freezing',
      'qc',
    ].map((kind) =>
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', kind, sessionId],
      }),
    ),
  );
}

function refreshAfterStaleSave(
  queryClient: QueryClient,
  error: unknown,
  sessionId: string,
) {
  if (isStaleVersionError(error)) {
    void invalidateOnFarmImplantSession(queryClient, sessionId);
  }
}

async function readProblems(response: Response): Promise<string[]> {
  try {
    const payload = (await response.json()) as {
      problems?: string[];
      message?: string;
    };
    if (payload?.problems?.length) {
      return payload.problems;
    }
    return payload?.message ? [payload.message] : [];
  } catch {
    return [];
  }
}

async function request<T>(
  token: AccessToken,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(token),
    ...init,
  });

  if (!response.ok) {
    throw new OnFarmImplantRequestError(
      response.status,
      await readProblems(response),
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

function matingsPath(
  sourceType: MatingsSourceType,
  searchParams: URLSearchParams,
): string {
  return `/api/on-farm-implant/matings/${sourceType}?${searchParams.toString()}`;
}

function buildMatingsParams({
  siteId,
  accountId,
  queryParams,
}: {
  siteId: string;
  accountId?: string;
  queryParams?: MatingsQueryParams;
}): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('siteId', siteId);
  if (accountId) {
    searchParams.set('accountId', accountId);
  }
  if (queryParams?.search) {
    searchParams.set('search', queryParams.search);
  }
  if (queryParams?.skip != null) {
    searchParams.set('skip', String(queryParams.skip));
  }
  if (queryParams?.take != null) {
    searchParams.set('take', String(queryParams.take));
  }
  if (queryParams?.order) {
    searchParams.set('order', queryParams.order);
  }
  return searchParams;
}

export const onFarmMatingsOptions = (
  token: AccessToken,
  {
    sourceType,
    siteId,
    accountId,
    queryParams,
  }: {
    sourceType: MatingsSourceType;
    siteId: string;
    accountId?: string;
    queryParams?: MatingsQueryParams;
  },
) =>
  queryOptions({
    queryKey: [
      'on-farm-implant',
      'matings',
      sourceType,
      { siteId, accountId, ...queryParams },
    ],
    enabled: !!token,
    async queryFn() {
      return request<OnFarmSourceSummary>(
        token,
        matingsPath(
          sourceType,
          buildMatingsParams({ siteId, accountId, queryParams }),
        ),
      );
    },
  });

export function createOnFarmMatingsOptions(
  token: AccessToken,
  sourceType: MatingsSourceType,
  siteId: string,
  accountId: string | undefined,
  searchParams: URLSearchParams,
) {
  return queryOptions({
    queryKey: [
      'on-farm-implant',
      'matings',
      sourceType,
      siteId,
      accountId,
      searchParams.toString(),
    ],
    enabled: !!token,
    async queryFn() {
      const base = new URLSearchParams(searchParams);
      base.set('siteId', siteId);
      if (accountId) {
        base.set('accountId', accountId);
      }

      return request<OnFarmSourceSummary>(token, matingsPath(sourceType, base));
    },
  });
}

export function createOnFarmSelectedMatingsOptions(
  token: AccessToken,
  sourceType: MatingsSourceType,
  siteId: string,
  recordKeys: string[],
  searchParams: URLSearchParams,
) {
  return queryOptions({
    queryKey: [
      'on-farm-implant',
      'matings',
      sourceType,
      'selected',
      siteId,
      recordKeys,
      searchParams.toString(),
    ],
    enabled: !!token,
    async queryFn() {
      const base = new URLSearchParams(searchParams);
      base.set('siteId', siteId);

      return request<OnFarmSourceSummary>(
        token,
        `/api/on-farm-implant/matings/${sourceType}/selected?${base.toString()}`,
        {
          method: 'POST',
          body: JSON.stringify({ records: recordKeys.map(fromRecordKey) }),
        },
      );
    },
  });
}

export const onFarmTotalsOptions = (
  token: AccessToken,
  {
    siteId,
    accountId,
  }: {
    siteId: string;
    accountId?: string;
  },
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'matings', 'totals', { siteId, accountId }],
    enabled: !!token,
    async queryFn() {
      const searchParams = new URLSearchParams();
      searchParams.set('siteId', siteId);
      if (accountId) {
        searchParams.set('accountId', accountId);
      }

      return request<OnFarmTotalsResponse>(
        token,
        `/api/on-farm-implant/matings/totals?${searchParams.toString()}`,
      );
    },
  });

export function buildSessionsParams(
  filters: OnFarmImplantSessionFilters,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  const { search, skip, take, ...rest } = filters;

  Object.entries(rest).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, String(value));
    }
  });

  if (search) {
    searchParams.set('search', search);
  }
  if (skip != null) {
    searchParams.set('skip', String(skip));
  }
  if (take != null) {
    searchParams.set('take', String(take));
  }

  return searchParams;
}

export const onFarmImplantSessionsOptions = (
  token: AccessToken,
  filters: OnFarmImplantSessionFilters = {},
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'sessions', filters],
    enabled: !!token,
    async queryFn() {
      return request<OnFarmImplantSessionsResponse>(
        token,
        `/api/on-farm-implant/sessions?${buildSessionsParams(filters).toString()}`,
      );
    },
  });

export const onFarmImplantSessionOptions = (
  token: AccessToken,
  sessionId: string,
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'session', sessionId],
    enabled: !!token,
    refetchInterval: ON_FARM_IMPLANT_BACKGROUND_REFRESH_MS,
    async queryFn() {
      return request<OnFarmImplantSession>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}`,
      );
    },
  });

export const onFarmImplantSessionFilterOptions = (
  token: AccessToken,
  siteId?: string,
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'session-filter-options', siteId],
    enabled: !!token,
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (siteId) {
        searchParams.set('siteId', siteId);
      }

      return request<OnFarmImplantSessionFilterOptions>(
        token,
        `/api/on-farm-implant/sessions/filter-options?${searchParams.toString()}`,
      );
    },
  });

export const onFarmImplantTripsOptions = (
  token: AccessToken,
  {
    search,
    take,
  }: {
    search?: string;
    take?: number;
  } = {},
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'trips', { search, take }],
    enabled: !!token,
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (search) {
        searchParams.set('search', search);
      }
      if (take != null) {
        searchParams.set('take', String(take));
      }

      return request<OnFarmImplantTrip[]>(
        token,
        `/api/on-farm-implant/trips?${searchParams.toString()}`,
      );
    },
  });

export function createOnFarmImplantSession(
  token: AccessToken,
  input: CreateOnFarmImplantSessionInput,
) {
  return request<OnFarmImplantSession>(token, '/api/on-farm-implant/sessions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateOnFarmImplantSession(
  token: AccessToken,
  sessionId: string,
  input: UpdateOnFarmImplantSessionInput,
) {
  return request<OnFarmImplantSession>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}`,
    {
      method: 'PUT',
      body: JSON.stringify(input),
    },
  );
}

export const useCreateOnFarmImplantSession = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOnFarmImplantSessionInput) =>
      createOnFarmImplantSession(token, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'sessions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session-filter-options'],
      });
    },
  });
};

export const useUpdateOnFarmImplantSession = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: UpdateOnFarmImplantSessionInput;
    }) => updateOnFarmImplantSession(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (session) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'sessions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', session.sessionId],
      });
    },
  });
};

export type OnFarmImplantApiSourceType = MatingsSourceType | 'non-tog';

export type OnFarmImplantUiSourceType =
  | 'fresh'
  | 'frozen'
  | 'flush'
  | 'client-on-farm-tog'
  | 'non-tog';

export type OnFarmImplantRecordKey = {
  recordId: string;
  accountId: string | null;
};

export type OnFarmImplantEmbryoPick = {
  embryoId: string;
  accountId: string | null;
  matingId: string;
  shipmentId: string | null;
  shipmentNumber: string | null;
};

export type OnFarmImplantSessionSource = {
  sourceTypeId: string;
  sourceType: OnFarmImplantApiSourceType;
  sequenceOrder: number;
  records: OnFarmImplantRecordKey[];
  embryos?: OnFarmImplantEmbryoPick[];
};

export type OnFarmImplantSelection = {
  sources: OnFarmImplantSessionSource[];
};

export type OnFarmImplantSourceSelectionRequest = {
  sourceType: OnFarmImplantApiSourceType;
  sequenceOrder: number;
  records: OnFarmImplantRecordKey[];
  embryos?: OnFarmImplantRecordKey[];
};

export type SaveSelectionRequest = {
  sources: OnFarmImplantSourceSelectionRequest[];
  versions?: RowVersions;
};

export type OnFarmImplantRecordIds = {
  sourceType: string;
  records: OnFarmImplantRecordKey[];
  totalRecords: number;
};

const RECORD_KEY_SEPARATOR = '|';

export function toRecordKey(
  recordId: string,
  accountId?: string | null,
): string {
  return `${recordId.toLowerCase()}${RECORD_KEY_SEPARATOR}${(accountId ?? '').toLowerCase()}`;
}

export function fromRecordKey(key: string): OnFarmImplantRecordKey {
  const separatorIndex = key.indexOf(RECORD_KEY_SEPARATOR);
  if (separatorIndex === -1) {
    return { recordId: key, accountId: null };
  }
  const accountId = key.slice(separatorIndex + 1);
  return {
    recordId: key.slice(0, separatorIndex),
    accountId: accountId || null,
  };
}

export const ON_FARM_IMPLANT_SOURCE_SEQUENCE: OnFarmImplantUiSourceType[] = [
  'fresh',
  'frozen',
  'flush',
  'client-on-farm-tog',
  'non-tog',
];

const UI_TO_API_SOURCE_TYPE: Record<
  OnFarmImplantUiSourceType,
  OnFarmImplantApiSourceType
> = {
  fresh: 'fresh',
  frozen: 'frozen',
  flush: 'flush',
  'client-on-farm-tog': 'client-on-farm',
  'non-tog': 'non-tog',
};

const API_TO_UI_SOURCE_TYPE: Record<
  OnFarmImplantApiSourceType,
  OnFarmImplantUiSourceType
> = {
  fresh: 'fresh',
  frozen: 'frozen',
  flush: 'flush',
  'client-on-farm': 'client-on-farm-tog',
  'non-tog': 'non-tog',
};

export function toApiSourceType(
  sourceType: string,
): OnFarmImplantApiSourceType {
  return (
    UI_TO_API_SOURCE_TYPE[sourceType as OnFarmImplantUiSourceType] ??
    (sourceType as OnFarmImplantApiSourceType)
  );
}

export function toUiSourceType(sourceType: string): OnFarmImplantUiSourceType {
  return (
    API_TO_UI_SOURCE_TYPE[sourceType as OnFarmImplantApiSourceType] ??
    (sourceType as OnFarmImplantUiSourceType)
  );
}

async function selectionRequest<T>(
  token: AccessToken,
  path: string,
  method: 'PUT' | 'DELETE' | 'POST',
  body?: unknown,
): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method,
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw new OnFarmImplantRequestError(
      response.status,
      await readProblems(response),
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const onFarmImplantSelectionOptions = (
  token: AccessToken,
  sessionId: string,
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'selection', sessionId],
    enabled: !!token,
    refetchInterval: ON_FARM_IMPLANT_BACKGROUND_REFRESH_MS,
    async queryFn() {
      return request<OnFarmImplantSelection>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/selection`,
      );
    },
  });

export const onFarmImplantFreshMatingsOptions = (
  token: AccessToken,
  sessionId: string,
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
    enabled: !!token,
    refetchInterval: ON_FARM_IMPLANT_BACKGROUND_REFRESH_MS,
    async queryFn() {
      return request<OnFarmMatingRecord[]>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/fresh-matings`,
      );
    },
  });

export const onFarmImplantFreshEmbryosOptions = (
  token: AccessToken,
  sessionId: string,
  matingId?: string,
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'fresh-embryos', sessionId, matingId],
    enabled: !!token,
    refetchInterval: ON_FARM_IMPLANT_BACKGROUND_REFRESH_MS,
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (matingId) searchParams.set('matingId', matingId);
      const query = searchParams.size ? `?${searchParams.toString()}` : '';
      return request<OnFarmImplantFreshEmbryo[]>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/fresh-embryos${query}`,
      );
    },
  });

export function createOnFarmImplantFreshEmbryos(
  token: AccessToken,
  sessionId: string,
  input: CreateOnFarmImplantFreshEmbryosRequest,
) {
  return selectionRequest<OnFarmImplantFreshEmbryo[]>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/fresh-embryos`,
    'POST',
    input,
  );
}

export const useCreateOnFarmImplantFreshEmbryos = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: CreateOnFarmImplantFreshEmbryosRequest;
    }) => createOnFarmImplantFreshEmbryos(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_embryos, { sessionId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
        }),
      ]),
  });
};

export function editOnFarmImplantFreshEmbryos(
  token: AccessToken,
  sessionId: string,
  input: EditOnFarmImplantFreshEmbryosRequest,
) {
  return selectionRequest<OnFarmImplantFreshEmbryoEditResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/fresh-embryos`,
    'PUT',
    input,
  );
}

export function discardOnFarmImplantFreshEmbryos(
  token: AccessToken,
  sessionId: string,
  sessionEmbryoIds: string[],
  versions?: RowVersions,
) {
  return selectionRequest<OnFarmImplantFreshEmbryo[]>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/fresh-embryos/discard`,
    'POST',
    { sessionEmbryoIds, versions },
  );
}

export const useDiscardOnFarmImplantFreshEmbryos = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      sessionEmbryoIds,
      versions,
    }: {
      sessionId: string;
      sessionEmbryoIds: string[];
      versions?: RowVersions;
    }) =>
      discardOnFarmImplantFreshEmbryos(
        token,
        sessionId,
        sessionEmbryoIds,
        versions,
      ),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_discarded, { sessionId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
        }),
      ]),
  });
};

export function recoverOnFarmImplantFreshEmbryos(
  token: AccessToken,
  sessionId: string,
  sessionEmbryoIds: string[],
  versions?: RowVersions,
) {
  return selectionRequest<OnFarmImplantFreshEmbryo[]>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/fresh-embryos/recover`,
    'POST',
    { sessionEmbryoIds, versions },
  );
}

export const useRecoverOnFarmImplantFreshEmbryos = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      sessionEmbryoIds,
      versions,
    }: {
      sessionId: string;
      sessionEmbryoIds: string[];
      versions?: RowVersions;
    }) =>
      recoverOnFarmImplantFreshEmbryos(
        token,
        sessionId,
        sessionEmbryoIds,
        versions,
      ),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_recovered, { sessionId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
        }),
      ]),
  });
};

export function saveOnFarmImplantFreshDegenerates(
  token: AccessToken,
  sessionId: string,
  matingId: string,
  degenerates: number,
  version?: string | null,
) {
  return selectionRequest<void>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/fresh-matings/${matingId}/degenerates`,
    'PUT',
    { degenerates, versions: version ? { [matingId]: version } : undefined },
  );
}

export const useSaveOnFarmImplantFreshDegenerates = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      matingId,
      degenerates,
      version,
    }: {
      sessionId: string;
      matingId: string;
      degenerates: number;
      version?: string | null;
    }) =>
      saveOnFarmImplantFreshDegenerates(
        token,
        sessionId,
        matingId,
        degenerates,
        version,
      ),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_result, { sessionId }) =>
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
      }),
  });
};

export const useEditOnFarmImplantFreshEmbryos = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: EditOnFarmImplantFreshEmbryosRequest;
    }) => editOnFarmImplantFreshEmbryos(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: async (response, { sessionId }) => {
      if (!response.applied) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'queue', sessionId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
        }),
      ]);
    },
  });
};

export type CreateOnFarmImplantNonTogEmbryosRequest = {
  donorId: string;
  sireId: string;
  freezeOwnerId: string;
  transferOwnerId?: string | null;
  breedId?: string | null;
  method?: number | null;
  recoveryFirmCode?: string | null;
  recoveryFirmName?: string | null;
  freshFrozen: number;
  sex?: number | null;
  implantDate: string;
  implantTierOf?: number | null;
  loadDonorPartners?: number | null;
  transferSiteId: string;
  transferClinicRanchHaulIn: number;
  transferTechName?: string | null;
  implantType?: number | null;
  recipientOwnerId?: string | null;
  deyardId?: string | null;
  implantedEmbryoStageGradeCount: OnFarmImplantStageGradeCount[];
};

const invalidateNonTogEmbryos = (
  queryClient: ReturnType<typeof useQueryClient>,
  sessionId: string,
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['on-farm-implant', 'session-embryos', sessionId, 'non-tog'],
    }),
    queryClient.invalidateQueries({
      queryKey: ['on-farm-implant', 'queue', sessionId],
    }),
  ]);

export function createOnFarmImplantNonTogEmbryos(
  token: AccessToken,
  sessionId: string,
  input: CreateOnFarmImplantNonTogEmbryosRequest,
) {
  return selectionRequest<OnFarmImplantSessionEmbryo[]>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/non-tog-embryos`,
    'POST',
    input,
  );
}

export const useCreateOnFarmImplantNonTogEmbryos = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: CreateOnFarmImplantNonTogEmbryosRequest;
    }) => createOnFarmImplantNonTogEmbryos(token, sessionId, input),
    onSuccess: (_embryos, { sessionId }) =>
      invalidateNonTogEmbryos(queryClient, sessionId),
  });
};

export function removeOnFarmImplantNonTogEmbryos(
  token: AccessToken,
  sessionId: string,
  sessionEmbryoIds: string[],
  versions?: RowVersions,
) {
  return selectionRequest<OnFarmImplantSessionEmbryo[]>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/non-tog-embryos/remove`,
    'POST',
    { sessionEmbryoIds, versions },
  );
}

export const useRemoveOnFarmImplantNonTogEmbryos = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      sessionEmbryoIds,
      versions,
    }: {
      sessionId: string;
      sessionEmbryoIds: string[];
      versions?: RowVersions;
    }) =>
      removeOnFarmImplantNonTogEmbryos(
        token,
        sessionId,
        sessionEmbryoIds,
        versions,
      ),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_embryos, { sessionId }) =>
      invalidateNonTogEmbryos(queryClient, sessionId),
  });
};

export const onFarmImplantRecordIdsOptions = (
  token: AccessToken,
  {
    sourceType,
    siteId,
    accountId,
    search,
    accountSearch,
  }: {
    sourceType: OnFarmImplantApiSourceType;
    siteId: string;
    accountId?: string;
    search?: string;
    accountSearch?: string;
  },
) =>
  queryOptions({
    queryKey: [
      'on-farm-implant',
      'record-ids',
      sourceType,
      { siteId, accountId, search, accountSearch },
    ],
    enabled: !!token,
    async queryFn() {
      const searchParams = new URLSearchParams();
      searchParams.set('siteId', siteId);
      if (accountId) {
        searchParams.set('accountId', accountId);
      }
      if (search) {
        searchParams.set('search', search);
      }
      if (accountSearch) {
        searchParams.set('accountSearch', accountSearch);
      }
      return request<OnFarmImplantRecordIds>(
        token,
        `/api/on-farm-implant/matings/${sourceType}/record-ids?${searchParams.toString()}`,
      );
    },
  });

export type OnFarmImplantSessionEmbryoStatus =
  | 'Selected'
  | 'Loaded'
  | 'Queued'
  | 'Discarded'
  | 'Frozen'
  | 'Thawed and Discarded'
  | 'On-Farm Freezing'
  | 'Unknown';

export type OnFarmImplantEmbryoInventoryStatus =
  | 'Frozen'
  | 'Implanted'
  | 'Discarded'
  | 'Shipped'
  | 'Used'
  | 'Thawed and Discarded'
  | 'AI'
  | 'Per Preg Pricing Hold'
  | 'Missing'
  | 'Unknown';

export type OnFarmImplantSessionEmbryo = {
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  accountId: string | null;
  accountName: string | null;
  recordName: string | null;
  frozenStrawNumber: number | null;
  frozenStrawLabel: string | null;
  cane: string | null;
  transferStrawNumber: number | null;
  transferStrawAssigned: boolean;
  status: number;
  statusName: OnFarmImplantSessionEmbryoStatus;
  freezeMethodId: string | null;
  freezeMethodName: string | null;
  grade: number | null;
  stage: number | null;
  freezeDate: string | null;
  embryoStatus: number | null;
  embryoStatusName: OnFarmImplantEmbryoInventoryStatus;
  donorName?: string | null;
  sireName?: string | null;
  canSendToOnFarmFreezing?: boolean;
  versions?: RowVersions;
};

export type OnFarmClientOnFarmEmbryo = {
  embryoId: string;
  embryoName: string | null;
  accountId: string | null;
  cane: string | null;
  strawNumber: number | null;
  stage: number | null;
  grade: number | null;
};

export const onFarmClientOnFarmMatingEmbryosOptions = (
  token: AccessToken,
  {
    matingId,
    accountId,
    search,
  }: {
    matingId: string;
    accountId?: string | null;
    search?: string;
  },
) =>
  queryOptions({
    enabled: !!token,
    queryKey: [
      'on-farm-implant',
      'client-on-farm',
      'mating-embryos',
      { matingId, accountId, search },
    ],
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (accountId) {
        searchParams.set('accountId', accountId);
      }
      if (search) {
        searchParams.set('search', search);
      }

      return request<OnFarmClientOnFarmEmbryo[]>(
        token,
        `/api/on-farm-implant/matings/client-on-farm/${matingId}/embryos?${searchParams.toString()}`,
      );
    },
  });

export const onFarmClientOnFarmSearchTotalsOptions = (
  token: AccessToken,
  {
    accountId,
    search,
    accountSearch,
  }: {
    accountId?: string;
    search?: string;
    accountSearch?: string;
  },
) =>
  queryOptions({
    enabled: !!token,
    queryKey: [
      'on-farm-implant',
      'client-on-farm',
      'totals',
      { accountId, search, accountSearch },
    ],
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (accountId) {
        searchParams.set('accountId', accountId);
      }
      if (search) {
        searchParams.set('search', search);
      }
      if (accountSearch) {
        searchParams.set('accountSearch', accountSearch);
      }

      return request<OnFarmSourceTotals>(
        token,
        `/api/on-farm-implant/matings/client-on-farm/totals?${searchParams.toString()}`,
      );
    },
  });

export type OnFarmImplantShipmentSourceType = 'fresh' | 'frozen';

export type OnFarmImplantShipmentSummary = {
  shipmentId: string;
  shipmentNumber: string;
  sourceType: OnFarmImplantShipmentSourceType;
  totalRecords: number;
  totalEmbryos: number;
};

export type OnFarmImplantShipmentContents = {
  shipmentId: string;
  shipmentNumber: string;
  sourceType: OnFarmImplantShipmentSourceType;
  records: OnFarmImplantRecordKey[];
  embryos: OnFarmImplantEmbryoPick[];
};

export const onFarmImplantShipmentsOptions = (
  token: AccessToken,
  {
    sourceType,
    search,
  }: {
    sourceType: OnFarmImplantShipmentSourceType;
    search: string;
  },
) =>
  queryOptions({
    enabled: !!token,
    queryKey: ['on-farm-implant', 'shipments', sourceType, { search }],
    async queryFn() {
      const searchParams = new URLSearchParams();
      searchParams.set('sourceType', sourceType);
      searchParams.set('search', search);

      return request<OnFarmImplantShipmentSummary[]>(
        token,
        `/api/on-farm-implant/shipments?${searchParams.toString()}`,
      );
    },
  });

export const onFarmImplantShipmentContentsOptions = (
  token: AccessToken,
  {
    sourceType,
    shipmentId,
  }: {
    sourceType: OnFarmImplantShipmentSourceType;
    shipmentId: string;
  },
) =>
  queryOptions({
    enabled: !!token,
    queryKey: ['on-farm-implant', 'shipments', sourceType, shipmentId],
    async queryFn() {
      return request<OnFarmImplantShipmentContents>(
        token,
        `/api/on-farm-implant/shipments/${sourceType}/${shipmentId}`,
      );
    },
  });

export type OnFarmImplantSessionEmbryos = {
  sourceType: string;
  sourceSelected: boolean;
  items: OnFarmImplantSessionEmbryo[];
  totalRecords: number;
};

export const ON_FARM_IMPLANT_ACTIONABLE_STATUSES: OnFarmImplantSessionEmbryoStatus[] =
  ['Selected', 'Loaded'];

export function isOnFarmImplantEmbryoActionable(
  embryo: OnFarmImplantSessionEmbryo,
): boolean {
  return (
    ON_FARM_IMPLANT_ACTIONABLE_STATUSES.includes(embryo.statusName) &&
    !!embryo.freezeMethodId
  );
}

export function isClientOnFarmEmbryoActionable(
  embryo: OnFarmImplantSessionEmbryo,
): boolean {
  return (
    ON_FARM_IMPLANT_ACTIONABLE_STATUSES.includes(embryo.statusName) &&
    embryo.embryoStatusName === 'Shipped'
  );
}

export function isNonTogEmbryoActionable(
  embryo: OnFarmImplantSessionEmbryo,
): boolean {
  return embryo.statusName === 'Loaded';
}

export const onFarmImplantSessionEmbryosOptions = (
  token: AccessToken,
  {
    sessionId,
    sourceType,
  }: {
    sessionId: string;
    sourceType: OnFarmImplantApiSourceType;
  },
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'session-embryos', sessionId, sourceType],
    enabled: !!token,
    refetchInterval: ON_FARM_IMPLANT_BACKGROUND_REFRESH_MS,
    async queryFn() {
      return request<OnFarmImplantSessionEmbryos>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/embryos/${sourceType}`,
      );
    },
  });

export function saveOnFarmImplantSelection(
  token: AccessToken,
  sessionId: string,
  input: SaveSelectionRequest,
) {
  return selectionRequest<OnFarmImplantSelection>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/selection`,
    'PUT',
    input,
  );
}

export function clearOnFarmImplantSelection(
  token: AccessToken,
  sessionId: string,
) {
  return selectionRequest<void>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/selection`,
    'DELETE',
  );
}

export function startOnFarmImplantSession(
  token: AccessToken,
  sessionId: string,
) {
  return selectionRequest<OnFarmImplantSession>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/start`,
    'POST',
  );
}

export type OnFarmImplantQueueItemInput = {
  sessionEmbryoId: string;
  transferStrawNumber: number | null;
};

export type AddOnFarmImplantQueueRequest = {
  items: OnFarmImplantQueueItemInput[];
  versions?: RowVersions;
};

export type OnFarmImplantQueueStatusName =
  | 'Pending'
  | 'Implanted'
  | 'Removed'
  | 'Unknown';

export type OnFarmImplantQueueItem = {
  sessionQueueId: string;
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  sourceType: string;
  queueOrder: number;
  transferStrawNumber: number | null;
  frozenStrawNumber: number | null;
  status: number;
  statusName: OnFarmImplantQueueStatusName;
};

export type OnFarmImplantQueueAddResponse = {
  queued: OnFarmImplantQueueItem[];
  totalQueued: number;
};

export type OnFarmImplantQueueRecord = {
  sessionQueueId: string;
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  sourceType: OnFarmImplantApiSourceType;
  queueOrder: number;
  accountId: string | null;
  accountName: string | null;
  recordName: string | null;
  transferStrawNumber: number | null;
  frozenStrawNumber: number | null;
  cane: string | null;
  freezeMethodId: string | null;
  freezeMethodName: string | null;
  grade: number | null;
  stage: number | null;
  embryoStatus: number;
  embryoStatusName: OnFarmImplantSessionEmbryoStatus;
  quality?: number | null;
  distance?: number | null;
  implantTechId?: string | null;
  implantTechName?: string | null;
  implantType?: number | null;
  implantTypeName?: string | null;
  farmLocationId?: string | null;
  farmLocationName?: string | null;
  recipientTag?: string | null;
  clSide?: number | null;
  transferComment?: string | null;
  dairyCompId?: number | null;
  implantDate?: string | null;
  isEditable?: boolean;
  missingFields?: OnFarmImplantQueueRequiredField[];
  canSendToOnFarmFreezing?: boolean;
  versions?: RowVersions;
};

export type OnFarmImplantQueueRequiredField = 'recipientTag' | 'implantType';

export type OnFarmImplantQueueEditableField =
  | 'quality'
  | 'distance'
  | 'implantTechId'
  | 'implantType'
  | 'farmLocationId'
  | 'recipientTag'
  | 'clSide'
  | 'transferComment'
  | 'dairyCompId'
  | 'implantDate';

export type OnFarmImplantQueueFieldValue = string | number | null;

export type OnFarmImplantQueueFieldChanges = Partial<
  Record<
    OnFarmImplantQueueEditableField,
    { value: OnFarmImplantQueueFieldValue }
  >
>;

export type UpdateOnFarmImplantQueueFieldsRequest = {
  sessionQueueIds: string[];
  changes: OnFarmImplantQueueFieldChanges;
  versions?: RowVersions;
};

export type AssignOnFarmImplantQueueBarcodeIdsRequest = {
  sessionQueueIds: string[];
  startingBarcodeId: number;
  versions?: RowVersions;
};

export const ON_FARM_IMPLANT_QUEUE_ROW_ONLY_FIELDS: readonly OnFarmImplantQueueEditableField[] =
  ['recipientTag', 'clSide', 'transferComment', 'dairyCompId'];

export type OnFarmImplantQueueLookupOptions = {
  implantTechs: OnFarmImplantLookupOption[];
  farmLocations: OnFarmImplantLookupOption[];
};

export type OnFarmImplantQueue = {
  items: OnFarmImplantQueueRecord[];
  totalRecords: number;
};

export const onFarmImplantQueueLookupOptions = (token: AccessToken) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'queue-options'],
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
    async queryFn() {
      return request<OnFarmImplantQueueLookupOptions>(
        token,
        '/api/on-farm-implant/queue-options',
      );
    },
  });

export function updateOnFarmImplantQueueFields(
  token: AccessToken,
  sessionId: string,
  input: UpdateOnFarmImplantQueueFieldsRequest,
) {
  return selectionRequest<OnFarmImplantQueue>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/queue/fields`,
    'PUT',
    input,
  );
}

export function recordOnFarmImplantPregChecks(
  token: AccessToken,
  sessionId: string,
  input: RecordOnFarmImplantPregChecksRequest,
) {
  return selectionRequest<OnFarmImplantPregCheckRow[]>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/preg-check/results`,
    'PUT',
    input,
  );
}

export function recordOnFarmImplantPregnancyLoss(
  token: AccessToken,
  sessionId: string,
  input: RecordOnFarmImplantPregnancyLossRequest,
) {
  return selectionRequest<OnFarmImplantPregCheckRow[]>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/preg-check/pregnancy-loss`,
    'PUT',
    input,
  );
}

export function assignOnFarmImplantQueueBarcodeIds(
  token: AccessToken,
  sessionId: string,
  input: AssignOnFarmImplantQueueBarcodeIdsRequest,
) {
  return selectionRequest<OnFarmImplantQueue>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/queue/barcode-ids`,
    'PUT',
    input,
  );
}

export function mergeOnFarmImplantQueueRecords(
  queue: OnFarmImplantQueue | undefined,
  updated: OnFarmImplantQueueRecord[],
): OnFarmImplantQueue | undefined {
  if (!queue) return queue;
  const byId = new Map(updated.map((row) => [row.sessionQueueId, row]));
  return {
    ...queue,
    items: queue.items.map((row) => byId.get(row.sessionQueueId) ?? row),
  };
}

const STALE_QUEUE_STATUSES: ReadonlySet<number> = new Set([400, 404, 409, 412]);

export const useRecordOnFarmImplantPregChecks = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: RecordOnFarmImplantPregChecksRequest;
    }) => recordOnFarmImplantPregChecks(token, sessionId, input),
    onSettled: (_data, _error, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'preg-check', 'worklist', sessionId],
      });
    },
  });
};

export const useRecordOnFarmImplantPregnancyLoss = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: RecordOnFarmImplantPregnancyLossRequest;
    }) => recordOnFarmImplantPregnancyLoss(token, sessionId, input),
    onSettled: (_data, _error, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'preg-check', 'worklist', sessionId],
      });
    },
  });
};

export const useUpdateOnFarmImplantQueueFields = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: UpdateOnFarmImplantQueueFieldsRequest;
    }) => updateOnFarmImplantQueueFields(token, sessionId, input),
    onSuccess: (response, { sessionId }) => {
      queryClient.setQueryData<OnFarmImplantQueue>(
        ['on-farm-implant', 'queue', sessionId],
        (queue) => mergeOnFarmImplantQueueRecords(queue, response.items),
      );
    },
    onError: (error, { sessionId }) => {
      if (
        !(error instanceof OnFarmImplantRequestError) ||
        !STALE_QUEUE_STATUSES.has(error.status)
      ) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'queue', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
    },
  });
};

export const useAssignOnFarmImplantQueueBarcodeIds = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: AssignOnFarmImplantQueueBarcodeIdsRequest;
    }) => assignOnFarmImplantQueueBarcodeIds(token, sessionId, input),
    onSuccess: (response, { sessionId }) => {
      queryClient.setQueryData<OnFarmImplantQueue>(
        ['on-farm-implant', 'queue', sessionId],
        (queue) => mergeOnFarmImplantQueueRecords(queue, response.items),
      );
    },
    onError: (error, { sessionId }) => {
      if (
        !(error instanceof OnFarmImplantRequestError) ||
        !STALE_QUEUE_STATUSES.has(error.status)
      ) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'queue', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
    },
  });
};

export const onFarmImplantQueueOptions = (
  token: AccessToken,
  {
    sessionId,
  }: {
    sessionId: string;
  },
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'queue', sessionId],
    enabled: !!token,
    refetchInterval: ON_FARM_IMPLANT_BACKGROUND_REFRESH_MS,
    async queryFn() {
      return request<OnFarmImplantQueue>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/queue`,
      );
    },
  });

export function addOnFarmImplantQueue(
  token: AccessToken,
  sessionId: string,
  sourceType: OnFarmImplantApiSourceType,
  input: AddOnFarmImplantQueueRequest,
) {
  return selectionRequest<OnFarmImplantQueueAddResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/queue/${sourceType}`,
    'POST',
    input,
  );
}

/**
 * 32829: thaw method options from the `tog_thawmethodid` optionset. Vitrified straws are
 * thawed differently from conventional ones, so the full optionset is offered here rather
 * than the shortened conventional-only list used on the embryo form.
 */
export const ON_FARM_IMPLANT_THAW_METHODS = [
  { value: 100000000, labelKey: 'conventionalChuteside' },
  { value: 100000001, labelKey: 'conventionalOuterlab' },
  { value: 100000002, labelKey: 'conventionalInnerlab' },
  { value: 100000003, labelKey: 'cutoutReloaded' },
  { value: 100000004, labelKey: 'directTransfer' },
  { value: 100000005, labelKey: 'vitrification' },
] as const;

export type RecordOnFarmImplantThawDiscardRequest = {
  items: { sessionEmbryoId: string }[];
  thawMethodId: number | null;
  thawTechId: string | null;
  thawThermosTemp?: string | null;
  versions?: RowVersions;
};

export type OnFarmImplantThawDiscardItem = {
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  status: number;
  statusName: OnFarmImplantSessionEmbryoStatus;
  thawMethodId: number | null;
  thawMethodName: string | null;
  thawById: string | null;
  thawByName: string | null;
};

export type OnFarmImplantThawDiscardResponse = {
  discarded: OnFarmImplantThawDiscardItem[];
  totalDiscarded: number;
};

export function recordOnFarmImplantThawDiscard(
  token: AccessToken,
  sessionId: string,
  input: RecordOnFarmImplantThawDiscardRequest,
) {
  return selectionRequest<OnFarmImplantThawDiscardResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/thaw-discard`,
    'POST',
    input,
  );
}

export type RecordOnFarmImplantFrozenDiscardRequest = {
  items: { sessionEmbryoId: string }[];
  versions?: RowVersions;
};

export type OnFarmImplantFrozenDiscardItem = {
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  grade: number | null;
  status: number;
  statusName: OnFarmImplantSessionEmbryoStatus;
};

export type OnFarmImplantFrozenDiscardResponse = {
  discarded: OnFarmImplantFrozenDiscardItem[];
  totalDiscarded: number;
};

export function recordOnFarmImplantFrozenDiscard(
  token: AccessToken,
  sessionId: string,
  input: RecordOnFarmImplantFrozenDiscardRequest,
) {
  return selectionRequest<OnFarmImplantFrozenDiscardResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/frozen-discard`,
    'POST',
    input,
  );
}

export type DiscardOnFarmImplantQueueRequest = {
  sessionQueueIds: string[];
  versions?: RowVersions;
};

export type OnFarmImplantQueueDiscardItem = {
  sessionQueueId: string;
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  sourceType: OnFarmImplantApiSourceType;
  queueOrder: number;
  embryoStatus: number;
  embryoStatusName: OnFarmImplantSessionEmbryoStatus;
};

export type OnFarmImplantQueueDiscardResponse = {
  discarded: OnFarmImplantQueueDiscardItem[];
  totalDiscarded: number;
};

export function discardOnFarmImplantQueueEmbryos(
  token: AccessToken,
  sessionId: string,
  input: DiscardOnFarmImplantQueueRequest,
) {
  return selectionRequest<OnFarmImplantQueueDiscardResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/queue/discard`,
    'POST',
    input,
  );
}

export const useSaveOnFarmImplantSelection = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: SaveSelectionRequest;
    }) => saveOnFarmImplantSelection(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_selection, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'selection', sessionId],
      });
    },
  });
};

export const useClearOnFarmImplantSelection = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      clearOnFarmImplantSelection(token, sessionId),
    onSuccess: (_result, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'selection', sessionId],
      });
    },
  });
};

export const useStartOnFarmImplantSession = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      startOnFarmImplantSession(token, sessionId),
    onSuccess: (_session, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'selection', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'sessions'],
      });
    },
  });
};

export const useAddOnFarmImplantQueue = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      sourceType,
      input,
    }: {
      sessionId: string;
      sourceType: OnFarmImplantApiSourceType;
      input: AddOnFarmImplantQueueRequest;
    }) => addOnFarmImplantQueue(token, sessionId, sourceType, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_result, { sessionId, sourceType }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session-embryos', sessionId, sourceType],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'queue', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
      if (sourceType === 'fresh') {
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
        });
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
        });
      }
    },
  });
};

export const useRecordOnFarmImplantThawDiscard = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: RecordOnFarmImplantThawDiscardRequest;
    }) => recordOnFarmImplantThawDiscard(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_result, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session-embryos', sessionId, 'frozen'],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
    },
  });
};

export const useRecordOnFarmImplantFrozenDiscard = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: RecordOnFarmImplantFrozenDiscardRequest;
    }) => recordOnFarmImplantFrozenDiscard(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_result, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session-embryos', sessionId, 'frozen'],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
    },
  });
};

export const useDiscardOnFarmImplantQueueEmbryos = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: DiscardOnFarmImplantQueueRequest;
    }) => discardOnFarmImplantQueueEmbryos(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_result, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'queue', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session-embryos', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
      });
    },
  });
};

export type SendOnFarmImplantToFreezingRequest = {
  sessionEmbryoIds: string[];
  versions?: RowVersions;
};

export type OnFarmImplantFreezingEmbryo = {
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  sourceType: OnFarmImplantApiSourceType;
  matingId: string | null;
  matingName: string | null;
  donorName: string | null;
  sireName: string | null;
  accountId: string | null;
  accountName: string | null;
  strawNumber: number | null;
  cane: string | null;
  stage: number | null;
  grade: number | null;
  embryoStatus: number | null;
  embryoStatusName: OnFarmImplantEmbryoInventoryStatus;
  sentFromImplantQueue: boolean;
  sentAt: string | null;
  freezeDate?: string | null;
  freezeTechId?: string | null;
  freezeTechName?: string | null;
  freezeMethodId?: string | null;
  freezeMethodName?: string | null;
  dtPlus?: boolean | null;
  storageLocationId?: string | null;
  storageLocationName?: string | null;
  freezer?: string | null;
  graderId?: string | null;
  graderName?: string | null;
  crackedZona?: boolean | null;
  missingFields?: OnFarmImplantFreezingRequiredField[];
  canComplete?: boolean;
  versions?: RowVersions;
};

export type OnFarmImplantFreezingResponse = {
  items: OnFarmImplantFreezingEmbryo[];
  totalRecords: number;
};

export type OnFarmImplantPregCheckImplantDate = {
  implantDate: string;
  recipientCount: number;
};

export type OnFarmImplantPregCheckResult = {
  checkNumber: number;
  checkDate: string | null;
  result: number | null;
  resultName: string | null;
  checkedById: string | null;
  checkedByName: string | null;
  procedureId: string | null;
  isPregnancyLoss?: boolean;
};

export const PREG_CHECK_RESULT = {
  OPEN: 100000000,
  PREGNANT: 100000001,
  COMPROMISED: 100000002,
  LEFT_THE_HERD: 100000003,
} as const;

export type RecordOnFarmImplantPregChecksRequest = {
  sessionQueueIds: string[];
  checkNumber?: number;
  result: number;
  checkDate?: string;
  checkedById?: string;
  versions?: RowVersions;
};

export type RecordOnFarmImplantPregnancyLossRequest = {
  sessionQueueIds: string[];
  lossDate: string;
  checkedById: string;
  versions?: RowVersions;
};

export type OnFarmImplantPregCheckRow = {
  sessionQueueId: string;
  sessionId: string;
  embryoId: string;
  embryoName: string | null;
  recipientTag: string | null;
  implantDate: string;
  sourceType: OnFarmImplantApiSourceType;
  matingName: string | null;
  donorName: string | null;
  sireName: string | null;
  accountId: string | null;
  accountName: string | null;
  technicianId: string | null;
  technicianName: string | null;
  clSide: number | null;
  checks: OnFarmImplantPregCheckResult[];
  implantStatus: number | null;
  implantStatusName: string | null;
  versions?: RowVersions;
};

export type OnFarmImplantPregCheckWorklistResponse = {
  implantDate: string;
  items: OnFarmImplantPregCheckRow[];
  totalRecords: number;
};

export type OnFarmImplantFreezingRequiredField =
  | 'freezeDate'
  | 'freezeTech'
  | 'freezeMethod'
  | 'dtPlus'
  | 'strawNumber'
  | 'cane'
  | 'storageLocation'
  | 'freezer'
  | 'grader'
  | 'account';

export type OnFarmImplantFreezingEditableField =
  | 'freezeDate'
  | 'freezeTechId'
  | 'freezeMethodId'
  | 'dtPlus'
  | 'strawNumber'
  | 'cane'
  | 'storageLocationId'
  | 'freezer'
  | 'graderId'
  | 'accountId'
  | 'crackedZona';

export type OnFarmImplantFreezingFieldValue = string | number | boolean | null;

export type OnFarmImplantFreezingFieldChanges = Partial<
  Record<
    OnFarmImplantFreezingEditableField,
    { value: OnFarmImplantFreezingFieldValue }
  >
>;

export type UpdateOnFarmImplantFreezingFieldsRequest = {
  sessionEmbryoIds: string[];
  changes: OnFarmImplantFreezingFieldChanges;
  versions?: RowVersions;
};

export type AssignOnFarmImplantFreezingStrawNumbersRequest = {
  sessionEmbryoIds: string[];
  startingStrawNumber?: number | null;
  versions?: RowVersions;
};

export type OnFarmImplantFreezingSelectionRequest = {
  sessionEmbryoIds: string[];
  versions?: RowVersions;
};

export type OnFarmImplantFreezingCompleteResponse = {
  completed: number;
};

export type OnFarmImplantFreezingLookupOptions = {
  techs: OnFarmImplantLookupOption[];
  freezeMethods: OnFarmImplantLookupOption[];
  storageLocations: OnFarmImplantLookupOption[];
  defaultFreezeMethodId: string | null;
};

export const onFarmImplantFreezingLookupOptions = (
  token: AccessToken,
  sessionId: string,
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'on-farm-freezing-options', sessionId],
    staleTime: 5 * 60 * 1000,
    enabled: !!token && !!sessionId,
    async queryFn() {
      return request<OnFarmImplantFreezingLookupOptions>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/on-farm-freezing/options`,
      );
    },
  });

export function updateOnFarmImplantFreezingFields(
  token: AccessToken,
  sessionId: string,
  input: UpdateOnFarmImplantFreezingFieldsRequest,
) {
  return selectionRequest<OnFarmImplantFreezingResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/on-farm-freezing/fields`,
    'PUT',
    input,
  );
}

export function assignOnFarmImplantFreezingStrawNumbers(
  token: AccessToken,
  sessionId: string,
  input: AssignOnFarmImplantFreezingStrawNumbersRequest,
) {
  return selectionRequest<OnFarmImplantFreezingResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/on-farm-freezing/straw-numbers`,
    'PUT',
    input,
  );
}

export function assignOnFarmImplantFreezingCanes(
  token: AccessToken,
  sessionId: string,
  input: OnFarmImplantFreezingSelectionRequest,
) {
  return selectionRequest<OnFarmImplantFreezingResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/on-farm-freezing/canes`,
    'PUT',
    input,
  );
}

export function completeOnFarmImplantFreezing(
  token: AccessToken,
  sessionId: string,
  input: OnFarmImplantFreezingSelectionRequest,
) {
  return selectionRequest<OnFarmImplantFreezingCompleteResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/on-farm-freezing/complete`,
    'POST',
    input,
  );
}

export function mergeOnFarmImplantFreezingRows(
  current: OnFarmImplantFreezingResponse | undefined,
  updated: OnFarmImplantFreezingEmbryo[],
): OnFarmImplantFreezingResponse | undefined {
  if (!current) return current;
  const byId = new Map(updated.map((row) => [row.sessionEmbryoId, row]));
  return {
    ...current,
    items: current.items.map((row) => byId.get(row.sessionEmbryoId) ?? row),
  };
}

const useFreezingEditMutation = <TInput>(
  edit: (
    token: AccessToken,
    sessionId: string,
    input: TInput,
  ) => Promise<OnFarmImplantFreezingResponse>,
) => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, input }: { sessionId: string; input: TInput }) =>
      edit(token, sessionId, input),
    onSuccess: (response, { sessionId }) => {
      queryClient.setQueryData<OnFarmImplantFreezingResponse>(
        ['on-farm-implant', 'on-farm-freezing', sessionId],
        (current) => mergeOnFarmImplantFreezingRows(current, response.items),
      );
    },
    onError: (error, { sessionId }) => {
      if (
        !(error instanceof OnFarmImplantRequestError) ||
        !STALE_QUEUE_STATUSES.has(error.status)
      ) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'on-farm-freezing', sessionId],
      });
    },
  });
};

export const useUpdateOnFarmImplantFreezingFields = () =>
  useFreezingEditMutation(updateOnFarmImplantFreezingFields);

export const useAssignOnFarmImplantFreezingStrawNumbers = () =>
  useFreezingEditMutation(assignOnFarmImplantFreezingStrawNumbers);

export const useAssignOnFarmImplantFreezingCanes = () =>
  useFreezingEditMutation(assignOnFarmImplantFreezingCanes);

export const useCompleteOnFarmImplantFreezing = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: OnFarmImplantFreezingSelectionRequest;
    }) => completeOnFarmImplantFreezing(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_result, { sessionId }) => {
      void invalidateOnFarmImplantSession(queryClient, sessionId);
    },
  });
};

export const onFarmImplantFreezingOptions = (
  token: AccessToken,
  {
    sessionId,
  }: {
    sessionId: string;
  },
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'on-farm-freezing', sessionId],
    enabled: !!token,
    async queryFn() {
      return request<OnFarmImplantFreezingResponse>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/on-farm-freezing`,
      );
    },
  });

export const onFarmImplantPregCheckImplantDatesOptions = (
  token: AccessToken,
  { sessionId }: { sessionId: string },
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'preg-check', 'implant-dates', sessionId],
    enabled: !!token,
    async queryFn() {
      return request<OnFarmImplantPregCheckImplantDate[]>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/preg-check/implant-dates`,
      );
    },
  });

export const onFarmImplantPregCheckWorklistOptions = (
  token: AccessToken,
  {
    sessionId,
    implantDate,
  }: {
    sessionId: string;
    implantDate: string;
  },
) =>
  queryOptions({
    queryKey: [
      'on-farm-implant',
      'preg-check',
      'worklist',
      sessionId,
      implantDate,
    ],
    enabled: !!token,
    async queryFn() {
      const searchParams = new URLSearchParams({ implantDate });
      return request<OnFarmImplantPregCheckWorklistResponse>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/preg-check/worklist?${searchParams.toString()}`,
      );
    },
  });

export type OnFarmImplantQcSourceType = 'fresh' | 'frozen' | 'flush';

export type OnFarmImplantFreshQcFields = {
  gunWarmerTemp: number | null;
  strawLot: string | null;
  slideWarmerTemp: number | null;
  ehmCreationDate: string | null;
  incubatorNumber: string | null;
  heatStageTemp: string | null;
};

export type OnFarmImplantFrozenQcFields = {
  thawThermosTemp: number | null;
  strayVoltageReading: number | null;
  gunWarmerTemp: number | null;
  ehmCreationDate: string | null;
  strawLot: string | null;
  incubatorNumber: string | null;
};

export type OnFarmImplantQcAudit = {
  sessionQcId: string;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  updatedById: string;
  updatedByName: string | null;
  updatedAt: string;
};

export type OnFarmImplantQcSection<T> = {
  values: T;
  audit: OnFarmImplantQcAudit | null;
};

export type OnFarmImplantQc = {
  freshSources: OnFarmImplantQcSourceType[];
  frozenSources: OnFarmImplantQcSourceType[];
  isEditable: boolean;
  fresh: OnFarmImplantQcSection<OnFarmImplantFreshQcFields> | null;
  frozen: OnFarmImplantQcSection<OnFarmImplantFrozenQcFields> | null;
  versions: RowVersions;
};

export type SaveOnFarmImplantQcRequest = {
  fresh?: OnFarmImplantFreshQcFields;
  frozen?: OnFarmImplantFrozenQcFields;
  versions?: RowVersions;
};

export const onFarmImplantQcOptions = (
  token: AccessToken,
  { sessionId }: { sessionId: string },
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'qc', sessionId],
    enabled: !!token,
    async queryFn() {
      return request<OnFarmImplantQc>(
        token,
        `/api/on-farm-implant/sessions/${sessionId}/qc`,
      );
    },
  });

export function saveOnFarmImplantQc(
  token: AccessToken,
  sessionId: string,
  input: SaveOnFarmImplantQcRequest,
) {
  return selectionRequest<OnFarmImplantQc>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/qc`,
    'PUT',
    input,
  );
}

export const useSaveOnFarmImplantQc = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: SaveOnFarmImplantQcRequest;
    }) => saveOnFarmImplantQc(token, sessionId, input),
    onSuccess: (qc, { sessionId }) => {
      queryClient.setQueryData(['on-farm-implant', 'qc', sessionId], qc);
    },
    onError: (error, { sessionId }) => {
      refreshAfterStaleSave(queryClient, error, sessionId);
    },
  });
};

export function sendOnFarmImplantToFreezing(
  token: AccessToken,
  sessionId: string,
  input: SendOnFarmImplantToFreezingRequest,
) {
  return selectionRequest<OnFarmImplantFreezingResponse>(
    token,
    `/api/on-farm-implant/sessions/${sessionId}/on-farm-freezing`,
    'POST',
    input,
  );
}

export const useSendOnFarmImplantToFreezing = () => {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: SendOnFarmImplantToFreezingRequest;
    }) => sendOnFarmImplantToFreezing(token, sessionId, input),
    onError: (error, { sessionId }) =>
      refreshAfterStaleSave(queryClient, error, sessionId),
    onSuccess: (_result, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'on-farm-freezing', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'queue', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session-embryos', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
      });
    },
  });
};

export const isOnFarmFreezingSelectionEligible = (
  rows: readonly { canSendToOnFarmFreezing?: boolean }[],
) =>
  rows.length > 0 && rows.every((row) => row.canSendToOnFarmFreezing === true);

export type EmbryoEditLock = {
  lockId: string;
  expiresAt: string;
  versions: RowVersions;
};

export type EmbryoEditLockHolder = {
  embryoId: string;
  userDisplayName: string | null;
};

export class EmbryoEditLockConflictError extends Error {
  readonly holders: EmbryoEditLockHolder[];

  constructor(holders: EmbryoEditLockHolder[]) {
    super('One or more embryos are being edited by someone else.');
    this.name = 'EmbryoEditLockConflictError';
    this.holders = holders;
  }
}

export async function acquireEmbryoEditLock(
  token: AccessToken,
  sessionId: string,
  embryoIds: string[],
): Promise<EmbryoEditLock> {
  const response = await fetch(
    `${getApiBaseUrl()}/api/on-farm-implant/sessions/${sessionId}/embryo-locks`,
    {
      method: 'POST',
      credentials: 'include',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ embryoIds }),
    },
  );

  if (response.status === 409) {
    const payload = (await response.json().catch(() => null)) as {
      holders?: EmbryoEditLockHolder[];
    } | null;
    throw new EmbryoEditLockConflictError(payload?.holders ?? []);
  }

  if (!response.ok) {
    throw new OnFarmImplantRequestError(
      response.status,
      await readProblems(response),
    );
  }

  return (await response.json()) as EmbryoEditLock;
}

export function renewEmbryoEditLock(token: AccessToken, lockId: string) {
  return selectionRequest<{ lockId: string; expiresAt: string }>(
    token,
    `/api/on-farm-implant/embryo-locks/${lockId}`,
    'PUT',
  );
}

export async function releaseEmbryoEditLock(
  token: AccessToken,
  lockId: string,
  { keepalive = false }: { keepalive?: boolean } = {},
) {
  const res = await fetch(
    `${getApiBaseUrl()}/api/on-farm-implant/embryo-locks/${lockId}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: getAuthHeaders(token),
      keepalive,
    },
  );

  if (!res.ok) {
    throw new OnFarmImplantRequestError(res.status, await readProblems(res));
  }
}
